import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { startGoogleSignIn } from "@/lib/googleAuth";
import { AdminShell } from "@/pages/AdminHome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = { backgroundColor: "#241246", border: "1px solid #1A0F3C", color: "#F5EFE3" };
const inputCls = "h-12 w-full rounded-md px-4 text-sm outline-none";
const goldBtn =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 h-11 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12] disabled:opacity-50";
const goldStyle = {
  backgroundColor: "transparent",
  color: "#F2D589",
  border: "1px solid #F2D589",
  letterSpacing: "0.18em",
  fontWeight: 500,
};

export default function VenuesAdmin() {
  const { user, loading, loginEmail } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/venues`);
      setText((data.venues || []).join("\n"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/venues`,
        { names: text.split("\n") },
        { withCredentials: true }
      );
      setText((data.venues || []).join("\n"));
      toast.success(`${(data.venues || []).length} communities saved.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminShell active="venues">
        <p className="mt-20 text-sm" style={{ color: "#C6BBA9" }}>
          One moment…
        </p>
      </AdminShell>
    );
  }

  if (!user) {
    return (
      <AdminShell active="venues">
        <div className="mt-16 max-w-[24rem]" data-testid="venues-admin-signin">
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
            Communities
          </h1>
          <button
            type="button"
            onClick={() => startGoogleSignIn("/admin/venues")}
            className={`${goldBtn} mt-8 w-full`}
            style={goldStyle}
            data-testid="venues-admin-google"
          >
            Continue with Google
          </button>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await loginEmail(email.trim(), password);
              } catch (err) {
                toast.error(formatApiErrorDetail(err));
              } finally {
                setBusy(false);
              }
            }}
            className="mt-8 space-y-3"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your address"
              className={inputCls}
              style={field}
              data-testid="venues-admin-email"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="password"
              className={inputCls}
              style={field}
              data-testid="venues-admin-password"
            />
            <button type="submit" disabled={busy} className={`${goldBtn} w-full`} style={goldStyle} data-testid="venues-admin-signin-submit">
              {busy ? "…" : "Sign in"}
            </button>
          </form>
        </div>
      </AdminShell>
    );
  }

  const count = text.split("\n").filter((l) => l.trim()).length;

  if (!user.is_admin) {
    return (
      <AdminShell active="venues">
        <p className="mt-20 text-sm" style={{ color: "#C6BBA9" }} data-testid="venues-admin-denied">
          {user.email} isn&apos;t on the admin list.
        </p>
      </AdminShell>
    );
  }

  return (
    <AdminShell active="venues">
      <div className="mt-14">
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
          Communities
        </h1>
        <p className="mt-3 text-sm" style={{ color: "#C6BBA9" }}>
          The scrolling list beside the Community Events hero. One community per
          line, in the order you want them read.
        </p>
      </div>

      <form onSubmit={save} className="mt-8 max-w-[42rem]" data-testid="venues-admin-form">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={18}
          spellCheck={false}
          className="w-full rounded-md px-4 py-3 text-sm outline-none"
          style={{ ...field, fontFamily: "'Courier Prime', monospace", lineHeight: 1.7 }}
          data-testid="venues-form-text"
        />
        <div className="mt-5 flex flex-wrap items-center gap-5">
          <button type="submit" disabled={saving} className={goldBtn} style={goldStyle} data-testid="venues-form-save">
            {saving ? "Saving…" : "Save list"}
          </button>
          <span className="text-[11px] uppercase" style={{ color: "#C6BBA9", letterSpacing: "0.16em" }}>
            {count} communities
          </span>
        </div>
      </form>
    </AdminShell>
  );
}
